import { CollapseProps } from 'antd';
import SHA256 from 'crypto-js/sha256';
import { DateTime, Duration } from 'luxon';
import { Environment } from 'react-relay';
import RelayModernEnvironment from 'relay-runtime/lib/store/RelayModernEnvironment';

import {
    COUNT_DAY_IN_WEEK,
    FORMAT_TIME_FOR_SAVE,
    GROUP_STATUS_DONE,
    GROUP_STATUS_IN_PROGRESS,
    GROUP_STATUS_TO_DO,
    HEIGHT_ITEM,
    HEIGHT_ONE_PERCENT,
    storeHeaderRows,
    WIDTH_COLUMN,
    WIDTH_COLUMN_MONTH,
    WIDTH_COLUMN_QUARTER,
    WIDTH_COLUMN_WEEK,
    WIDTH_COLUMN_YEAR,
} from './const';
import { processOrderingItemsAndSetNumbersWBS } from './gantChangeData';
import { IOutGeometryParam } from './hooks/useGetGeometryParamForItem';
import { store } from './store';
import { setterGantItems } from './store/slices/gantItemsSlice';
import { setMappingItemIdIndex, setterGant } from './store/slices/gantSlice';
import {
    BondType,
    CalendarScaleType,
    CalendarViewType,
    DayWeekType,
    DirectionDragItemType,
    DraggableGantType,
    HeaderType,
    ItemTaskType,
    ItemType,
    MappingItemIdHideCollapse,
    MappingItemIdIndexType,
    ModeTimeLineType,
    RowGantType,
    ScheduleItemType,
    ValueDateType,
} from './types';

export const countBusinessDays = (start: DateTime, end: DateTime): number => {
    let businessDays = 0;
    let currentDate = start;

    while (currentDate <= end) {
        const weekday = currentDate.weekday;
        if (weekday >= 1 && weekday <= 5) {
            businessDays++;
        }
        currentDate = currentDate.plus({ days: 1 });
    }

    return businessDays;
};

export const countWeekendDays = (start: DateTime, end: DateTime): number => {
    let weekendDays = 0;
    let currentDate = start;

    while (currentDate <= end) {
        const weekday = currentDate.weekday;
        if (weekday === 6 || weekday === 7) {
            weekendDays++;
        }
        currentDate = currentDate.plus({ days: 1 });
    }

    return weekendDays;
};

export const getEndDateGivenBusinessDays = (start: DateTime, businessDays: number): DateTime => {
    let currentDate = start;
    if (currentDate.weekday >= 1 && currentDate.weekday <= 5) businessDays--;
    while (businessDays > 0) {
        currentDate = currentDate.plus({ days: 1 });
        const weekday = currentDate.weekday;
        console.log(currentDate, businessDays, weekday);
        if (weekday >= 1 && weekday <= 5) {
            businessDays--;
        }
    }

    return currentDate;
};

export const getScreenViewHorizontal = (
    scrollX: number,
    widthColumn: number,
    availableWidth: number,
    widthLeftHeader: number,
) => {
    const daysLimitForDraw = Math.floor((availableWidth - widthLeftHeader) / widthColumn) + 2;
    const indexStart = Math.floor(scrollX / widthColumn);

    return {
        start: !indexStart ? indexStart : indexStart - 1,
        end: indexStart + daysLimitForDraw,
    };
};

export const getScreenViewVertical = (scrollY: number, height: number) => {
    const indexStart = Math.round(scrollY / HEIGHT_ITEM);
    const itemsLimitForDraw = Math.round(height / HEIGHT_ITEM);

    return { start: indexStart, end: indexStart + itemsLimitForDraw };
};

export const getWidthColumn = (value: HeaderType) => {
    let newWidthColumn;
    switch (value) {
        case 'day':
            newWidthColumn = WIDTH_COLUMN;
            break;
        case 'week':
            newWidthColumn = WIDTH_COLUMN_WEEK;
            break;
        case 'month':
            newWidthColumn = WIDTH_COLUMN_MONTH;
            break;
        case 'quarter':
            newWidthColumn = WIDTH_COLUMN_QUARTER;
            break;
        case 'year':
            newWidthColumn = WIDTH_COLUMN_YEAR;
            break;
    }

    return newWidthColumn;
};

export const filterTaskForCalendar = (currentDate: string, type: DayWeekType) => {
    const startWeekDate = getStartOfWeek(DateTime.fromISO(currentDate));
    const endWeekDate = getEndOfWeek(DateTime.fromISO(currentDate));

    return (task: ItemTaskType) => {
        if (type === 'day') {
            return currentDate === task.datetime;
        }
        const taskDate = DateTime.fromISO(task.datetime);
        return taskDate.toSeconds() >= startWeekDate.toSeconds() && taskDate.toSeconds() <= endWeekDate.toSeconds();
    };
};

export const optimizeEmptyDayWeek = (itemsDataByWeekDay: (ItemType | null)[][]): (ItemType | null)[][] => {
    const inputItemsDataByWeekDay = itemsDataByWeekDay.filter((items) => items.filter((item) => item).length);
    const counterList = [];
    const counterListMirror = [];
    let isChanged = false;

    for (const itemDayWeek of inputItemsDataByWeekDay) {
        let countEmptyBefore = 0;
        let countEmptyAfter = 0;
        let countFull = 0;

        for (const item of itemDayWeek) {
            if (item) {
                countFull++;
            } else {
                if (!countFull) {
                    countEmptyBefore--;
                } else {
                    countEmptyAfter--;
                }
            }
        }

        const newCounter = [];
        if (countEmptyBefore < 0) {
            newCounter.push(countEmptyBefore);
        }
        if (countFull > 0) {
            newCounter.push(countFull);
        }
        if (countEmptyAfter < 0) {
            newCounter.push(countEmptyAfter);
        }

        counterList.push(newCounter);
        counterListMirror.push(newCounter.map((c) => c * -1));
    }

    for (let i = 0; i < counterList.length; i++) {
        if (i + 1 >= counterListMirror.length) {
            break;
        }

        for (let j = i + 1; j < counterListMirror.length; j++) {
            if (
                counterList[i].join('') === counterListMirror?.[j].join('') ||
                (counterList[i][0] > 0 && counterList[i][0] <= counterListMirror?.[j][0]) ||
                (-1 * counterList[i][0] + counterList[i][1] > 0 &&
                    -1 * counterList[i][0] + counterList[i][1] <= counterListMirror?.[j][0])
            ) {
                for (let k = 0; k < inputItemsDataByWeekDay[i].length; k++) {
                    if (!inputItemsDataByWeekDay[i][k]) {
                        inputItemsDataByWeekDay[i][k] = inputItemsDataByWeekDay[j][k];
                    }
                }
                inputItemsDataByWeekDay.splice(j, 1);
                counterListMirror.splice(j, 1);
                isChanged = true;
                break;
            }
        }
    }

    if (isChanged) {
        return optimizeEmptyDayWeek(inputItemsDataByWeekDay);
    }
    return inputItemsDataByWeekDay;
};

export const getTimeFromNumberRow = (row: number) => {
    return Duration.fromObject({ minutes: row * 30 }).toFormat(FORMAT_TIME_FOR_SAVE);
};

export const getDiffDayFromDateString = (firstDate: string, secondDate: string) => {
    const oldEndDate = DateTime.fromISO(firstDate);
    const newEndDate = DateTime.fromISO(secondDate);
    return newEndDate.diff(oldEndDate, 'days').days;
};

export const getValueByHeaderType = (
    rowType: RowGantType | HeaderType,
    date: DateTime,
    isShortForm: boolean,
): ValueDateType => {
    switch (rowType) {
        case 'dayWeek':
            return {
                valueGroup: date.toFormat(isShortForm ? 'ccc' : 'cccc'), // day week Mon | Monday
                valueDisplay: date.toFormat(isShortForm ? 'ccc' : 'cccc'),
                valueTip: date.toFormat('cccc, dd'),
            };
        case 'week':
        case 'weekNum':
            return {
                valueGroup: getCustomWeekNumber(date).toString(),
                valueDisplay: date.toFormat('EEE dd MMM yyyy'),
                valueTip: date.toFormat('EEE dd MMM yyyy'),
            };
        case 'weekNum2':
            return {
                valueGroup: getCustomWeekNumber(date).toString(),
                valueDisplay: `${date.toFormat('MMM')}${date.year !== DateTime.now().year ? " '" + date.toFormat('yy') : ''}`,
            };
        case 'weekNum3':
            return {
                valueGroup: getCustomWeekNumber(date).toString(),
                valueDisplay: getCustomWeekNumber(date).toString(),
            };
        case 'quarter':
            const firstNumberMonthOfQuarter = (date.quarter - 1) * 3 + 1;
            const lastNumberMonthOfQuarter = date.quarter * 3;

            const firstMonthOfQuarter = DateTime.local()
                .set({
                    month: firstNumberMonthOfQuarter,
                    year: date.year,
                })
                .toFormat('MMM');
            const lastMonthOfQuarter = DateTime.local()
                .set({
                    month: lastNumberMonthOfQuarter,
                    year: date.year,
                })
                .toFormat('MMM');
            return {
                valueGroup: `Q${date.quarter}${date.year}`,
                valueDisplay: `${firstMonthOfQuarter} - ${lastMonthOfQuarter}${date.year !== DateTime.now().year ? " '" + date.toFormat('yy') : ''}`,
            };
        case 'month':
            return {
                valueGroup: date.toFormat(isShortForm ? 'MMM_yy' : 'MMMM_yy'),
                valueDisplay: `${date.toFormat('MMM')}${date.year !== DateTime.now().year ? " '" + date.toFormat('yy') : ''}`,
            };
        case 'month2':
            return {
                valueGroup: date.toFormat(isShortForm ? 'MMM' : 'MMMM'),
                valueDisplay: date.toFormat('MMM'),
            };
        case 'year':
            return {
                valueGroup: date.year.toString(),
                valueDisplay: date.year.toString(),
            };
    }
    return {
        valueGroup: date.toFormat('dd'),
        valueDisplay: date.toFormat('dd'),
        valueTip: date.toFormat('cccc, dd'),
    };
};

export const getHeaderRowsByHeaderType = (headerType: HeaderType, _modeTimeLine: ModeTimeLineType) => {
    // if (modeTimeLine === 'pagination') {
    //     return ['quarter', 'year'].includes(headerType) ? storeHeaderRows['week'] : storeHeaderRows['day'];
    // }
    return storeHeaderRows[headerType];
};

export const getItemBorderDate = (item: ItemType) => {
    const itemStartDate = DateTime.fromISO(item.startDate);
    const itemEndDate = DateTime.fromISO(item.endDate);

    return { itemStartDate, itemEndDate };
};

export const getItemsDataForWeek = (setOfDaysWeek: DateTime[], filteredItems: ItemType[]) => {
    const checkItemForInclude = (dateOfWeek: DateTime, item: ItemType | null) => {
        if (!item) {
            return false;
        }
        const { itemStartDate, itemEndDate } = getItemBorderDate(item);

        return (
            dateOfWeek.toSeconds() - 24 * 60 * 60 >= itemStartDate.toSeconds() &&
            dateOfWeek.toSeconds() <= itemEndDate.toSeconds() + 24 * 60 * 60
        );
    };

    const itemsDataByWeekDay = filteredItems.map((item) => {
        return setOfDaysWeek.map((date) => (checkItemForInclude(date, item) ? item : null));
    });

    return optimizeEmptyDayWeek(itemsDataByWeekDay as (ItemType | null)[][]);
};

export const getBorderDateTime = (
    viewCalendar: CalendarViewType,
    currentDateTime: DateTime,
    agendaFilter: HeaderType,
    scheduleScale: CalendarScaleType,
) => {
    let startBorderDate = currentDateTime.toSeconds();
    let endBorderDate = currentDateTime.toSeconds();

    switch (viewCalendar) {
        case 'schedule':
            switch (scheduleScale) {
                case 'month':
                    startBorderDate = currentDateTime.startOf('month').minus({ days: COUNT_DAY_IN_WEEK }).toSeconds();
                    endBorderDate = currentDateTime
                        .endOf('month')
                        .plus({ days: COUNT_DAY_IN_WEEK * 2 })
                        .toSeconds();
                    break;
                case 'quarter':
                case 'year':
                case 'week':
                    startBorderDate = getStartOfWeek(currentDateTime).toSeconds();
                    endBorderDate = getEndOfWeek(currentDateTime).toSeconds();
                    break;
            }
            break;
        case 'list':
            startBorderDate = currentDateTime.startOf('month').minus({ days: COUNT_DAY_IN_WEEK }).toSeconds();
            endBorderDate = currentDateTime
                .endOf('month')
                .plus({ days: COUNT_DAY_IN_WEEK * 2 })
                .toSeconds();
            break;
        case 'agenda': {
            if (agendaFilter !== 'day') {
                startBorderDate =
                    agendaFilter === 'week'
                        ? getStartOfWeek(currentDateTime).toSeconds()
                        : currentDateTime.startOf(agendaFilter).toSeconds();

                endBorderDate =
                    agendaFilter === 'week'
                        ? getEndOfWeek(currentDateTime).toSeconds()
                        : currentDateTime.endOf(agendaFilter).toSeconds();
            }
            break;
        }
    }

    return { startBorderDate, endBorderDate };
};

export const getRandomInt = (min: number, max: number): number => {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min + 1)) + min;
};

export const hashString = (message: string) => {
    return SHA256(message).toString();
};

export const getDirectionOfMovingItem = (dragItem: ItemType, dropItem: ItemType): DirectionDragItemType => {
    if (dragItem.idIndex! > dropItem.idIndex!) {
        return 'up'; // item is moving UP
    }
    if (dragItem?.idIndex! < dropItem.idIndex!) {
        return 'down'; // item is moving DOWN
    }
    return 'none'; // item isn't moving
};

export const calcStatusByPercentageCompletion = (percent: number) => {
    switch (percent) {
        case 0:
            return GROUP_STATUS_TO_DO;
        case 100:
            return GROUP_STATUS_DONE;
        default:
            return GROUP_STATUS_IN_PROGRESS;
    }
};

const MAX_DEEP_LEVEL = 5;

export const getObjectFromRelayStoreById = <T>(
    environment: Environment,
    objectId: string,
    level: number = 0,
): T | null | undefined => {
    const object = { ...(environment?.getStore().getSource().get(objectId) as { [key: string]: any }) };

    if (MAX_DEEP_LEVEL === level) {
        return object as T;
    }

    const newLevel = level + 1;
    for (const key of Object.keys(object)) {
        if ((object as any)[key]?.['__ref']) {
            object[key] = getObjectFromRelayStoreById(environment, (object as any)[key]['__ref'], newLevel);
        }
        if ((object as any)[key]?.['__refs']) {
            const arrayObject = [];
            for (const id of (object as any)[key]?.['__refs']) {
                arrayObject.push(getObjectFromRelayStoreById(environment, id, newLevel));
            }
            object[key] = arrayObject;
        }
    }
    return object as T;
};

export const CUSTOM_WEEK_START = 0;

export const getSetOfWeekDateByDate = (currentDate: string) => {
    const startDayWeek = getStartOfWeek(DateTime.fromISO(currentDate));
    return Array.from({ length: COUNT_DAY_IN_WEEK }, (_, i) => i).map((dayIndex) =>
        startDayWeek.plus({ day: dayIndex + 1 }),
    );
};

export const getStartOfWeek = (date: DateTime): DateTime => {
    const weekday = date.weekday;
    const diff = (weekday - CUSTOM_WEEK_START + 7) % 7;
    return date.minus({ days: diff }).startOf('day');
};

export const getEndOfWeek = (date: DateTime): DateTime => {
    const weekday = date.weekday;
    const diff = (CUSTOM_WEEK_START + 6 - weekday + 7) % 7;
    return date.plus({ days: diff }).endOf('day');
};

export const getCustomWeekNumber = (date: DateTime): number => {
    const currentWeekday = date.weekday % 7;
    const offset = (currentWeekday - CUSTOM_WEEK_START + 7) % 7;

    const adjustedDate = date.minus({ days: offset });
    return adjustedDate.weekNumber;
};

export const getCustomWeekday = (date: DateTime): number => {
    const standardWeekday = date.weekday; // 1–7
    return (standardWeekday - 1 + 7 - CUSTOM_WEEK_START) % 7;
};

export const getKeyCollapse = (collapseItems: CollapseProps['items']) =>
    collapseItems?.map((col) => col.key) as string[];

export const capitalizeFirstLetter = (str: string) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
};

export const calcParents = (
    item: ItemType,
    bufferParentId: string[],
    items: ItemType[],
    mappingItemIdIndex: MappingItemIdIndexType,
): number => {
    if (item?.parentId) {
        bufferParentId.push(item.parentId);
        const nextItem = items[mappingItemIdIndex[item.parentId]];

        if (nextItem && !bufferParentId.includes(nextItem.parentId || '')) {
            return calcParents(items[mappingItemIdIndex[item.parentId]], bufferParentId, items, mappingItemIdIndex) + 1;
        }
        return 0;
    }

    return 1;
};

export const getPointXByGeometryParamDragItem = (dragging: DraggableGantType, geometryParamItem: IOutGeometryParam) => {
    return dragging === 'bondLeft'
        ? geometryParamItem.startX - HEIGHT_ONE_PERCENT * 5
        : geometryParamItem.endX + HEIGHT_ONE_PERCENT * 5;
};

export const getPointYByGeometryParamDragItem = (geometryParamItem: IOutGeometryParam) => {
    return geometryParamItem.startY + HEIGHT_ONE_PERCENT * 50;
};

export const getTypeBondByDragging = (draggingStart: DraggableGantType, draggingEnd: DraggableGantType): BondType => {
    if (draggingStart === 'bondLeft' && draggingEnd === 'bondLeft') {
        return 'SS';
    }
    if (draggingStart === 'bondLeft' && draggingEnd === 'bondRight') {
        return 'SE';
    }
    if (draggingStart === 'bondRight' && draggingEnd === 'bondLeft') {
        return 'ES';
    }
    if (draggingStart === 'bondRight' && draggingEnd === 'bondRight') {
        return 'EE';
    }
    return 'SS';
};

export const getBondNameByBondType = (typeBond: BondType, isParentName?: boolean): keyof ItemType => {
    return isParentName ? `bondParent${typeBond}` : `bond${typeBond}`;
};

export const findItemFirstChild = (
    fieldName: keyof Pick<
        ItemType,
        | 'children'
        | 'bondLeft'
        | 'bondRight'
        | 'bondParentSS'
        | 'bondParentSE'
        | 'bondParentES'
        | 'bondParentEE'
        | 'bondSS'
        | 'bondSE'
        | 'bondES'
        | 'bondEE'
    >,
    item: ItemType | undefined,
    items: ItemType[],
    indexIdMap: MappingItemIdIndexType,
) => {
    if (!item?.[fieldName] || !item?.[fieldName]?.length) {
        return undefined;
    }

    for (const childId of item[fieldName]!) {
        const child = items[indexIdMap[childId]];
        if (child) {
            return child;
        }
    }

    return undefined;
};

export const findPhaseScheduleItem = (scheduleItemId: string, environment: RelayModernEnvironment): number | null => {
    const scheduleItem = getObjectFromRelayStoreById(environment, scheduleItemId) as ScheduleItemType;
    if (scheduleItem.role_item === 'project') {
        return null;
    }

    if (scheduleItem.phase_id && scheduleItem.role_item === 'phase') {
        return scheduleItem.phase_id;
    } else {
        return findPhaseScheduleItem(scheduleItem.parent_id, environment);
    }
};

export const getDateScaled = (currentDate: string | DateTime, scale: CalendarScaleType) => {
    const currentDateTime = currentDate instanceof DateTime ? currentDate : DateTime.fromSQL(currentDate);
    return scale === 'week' ? currentDateTime.minus({ days: currentDateTime.weekday % 7 }) : currentDateTime;
};

export const getDateNotLess = ({
    currentDate,
    notLessDate,
    fallbackDate,
}: {
    currentDate: DateTime;
    notLessDate: DateTime;
    fallbackDate: DateTime;
}) => {
    return currentDate.diff(notLessDate).as('seconds') < 0 ? fallbackDate : currentDate;
};

export const getItemsToExpand = (
    item: ItemType,
    itemsList: ItemType[],
    mappingItemIdIndex: MappingItemIdIndexType,
): ItemType[] => {
    let itemsToExpand: ItemType[] = [];

    if (item.hideForCollapse) {
        itemsToExpand.push(item);
    }

    if (item.parentId) {
        const parentIndex = mappingItemIdIndex[item.parentId];
        if (parentIndex !== undefined) {
            const parentItem = itemsList[parentIndex];

            itemsToExpand = itemsToExpand.concat(getItemsToExpand(parentItem, itemsList, mappingItemIdIndex));

            if (parentItem.children && parentItem.children.length > 0) {
                parentItem.children.forEach((childId) => {
                    const childIndex = mappingItemIdIndex[childId];
                    if (childIndex !== undefined) {
                        const childItem = itemsList[childIndex];
                        if (childItem.hideForCollapse) {
                            itemsToExpand.push(childItem);
                        }
                    }
                });
            }
        }
    }

    return Array.from(new Set(itemsToExpand));
};

export const getExpandedItemsListForRedux = (
    itemsList: ItemType[],
    itemsToExpand: ItemType[],
    currentItem: ItemType,
): { itemsList: ItemType[] } | undefined => {
    if (itemsToExpand.length === 0) {
        return undefined;
    }

    let index = 0;
    const updatedItemsList = itemsList.map((item) => {
        const shouldExpand = itemsToExpand.some((collapsed) => collapsed.id === item.id);
        const newItem = shouldExpand ? { ...item, hideForCollapse: false } : item;
        const idIndex = !newItem.hideForCollapse ? index++ : -1;

        if (currentItem.id === newItem.id) {
            currentItem.idIndex = idIndex;
        }
        return {
            ...newItem,
            idIndex,
        };
    });

    return { itemsList: updatedItemsList };
};

export const dispatchItemsList = (inputItems: ItemType[]) => {
    let index = 0;
    const { mappingScheduleItemIdIndex, newItems, newInputItems } = processOrderingItemsAndSetNumbersWBS(
        inputItems.map((item) => ({
            ...item,
            hideForCollapse: false,
            idIndex: !item.hideForCollapse ? index++ : -1,
        })),
    );

    store.dispatch(
        setterGantItems({
            inputItems: newInputItems,
            itemsList: newItems,
        }),
    );

    store.dispatch(
        setterGant({
            mappingInputItemIdIndex: mappingScheduleItemIdIndex,
        }),
    );
    store.dispatch(setMappingItemIdIndex(mappingScheduleItemIdIndex));
};

export const locateItemVertically = (
    newCurrentItem: ItemType,
    itemsList: ItemType[],
    mappingItemIdIndex: MappingItemIdIndexType,
    mappingItemIdHideCollapse: MappingItemIdHideCollapse,
    availableHeight: number,
) => {
    const itemsToExpand = getItemsToExpand(newCurrentItem, itemsList, mappingItemIdIndex);
    const newItemsList = getExpandedItemsListForRedux(itemsList, itemsToExpand, newCurrentItem);

    const verticalCenterScrollPosition =
        (mappingItemIdIndex[newCurrentItem.id] ?? 0) * HEIGHT_ITEM + HEIGHT_ITEM / 2 - availableHeight / 2;
    const newScrollY = Math.max(0, verticalCenterScrollPosition);

    const newMappingItemIdHideCollapse =
        itemsToExpand.length > 0 ? { ...mappingItemIdHideCollapse } : mappingItemIdHideCollapse;
    itemsToExpand.forEach((item) => {
        newMappingItemIdHideCollapse[item.id] = item.hideForCollapse ?? false;
    });
    return { newItemsList, newMappingItemIdHideCollapse, newScrollY };
};

export const getColorStatus = (status?: number | null) => {
    //id from list task_group_status
    switch (status) {
        // ['id' => 376, 'type' => 'task_group_status', 'name' => 'To Do', 'child' => '401,402'],
        case 376:
            return '#aeaeae'; //'To Do'
        // ['id' => 377, 'type' => 'task_group_status', 'name' => 'In Progress', 'child' => '403,404'],
        case 377:
            return '#5ac7e8'; //'In Progress'
        // ['id' => 378, 'type' => 'task_group_status', 'name' => 'Done', 'child' => '405,406'],
        case 378:
            return '#79c355'; //'Done'
        default:
            return 'white';
    }
};

export const combineFilterFlexible = (filterFlexible1?: string | null, filterFlexible2?: string | null) => {
    if (filterFlexible1 && filterFlexible2) {
        const filterFlexibleObject = JSON.parse(filterFlexible2);
        const currentFilterObject = JSON.parse(filterFlexible1);

        if (filterFlexibleObject.or && currentFilterObject.or) {
            return JSON.stringify({ or: [...filterFlexibleObject.or], and: currentFilterObject });
        }

        return JSON.stringify([...filterFlexibleObject, currentFilterObject]);
    }

    if (filterFlexible2) {
        return filterFlexible2;
    }

    if (filterFlexible1) {
        const currentFilterObject = JSON.parse(filterFlexible1);
        if (Array.isArray(currentFilterObject)) {
            return filterFlexible1;
        }
        return `[${filterFlexible1}]`;
    }

    return undefined;
};

export const checkAssigneeData = (assigneeData?: string | string[]) => {
    if (!assigneeData) {
        return [];
    }

    if (Array.isArray(assigneeData)) {
        return assigneeData;
    }

    return assigneeData.split(',');
};
